'use client';

import React, { useMemo, useState } from 'react';
import { Layout } from '@benzinga/core-ui';
import { WatchVideoCard } from './WatchVideoCard';
import { WatchEmptyState } from './WatchEmptyState';
import { WordpressSidebar } from '@benzinga/content-manager';
import { MoneySidebar } from '@benzinga/money';
import DefaultSidebar from '../../../src/components/Sidebars/DefaultSidebar';
import { VIDEO_FILTER_CATEGORIES, VideoCategoryGroup, VideoCategoryKey } from './utils';
import { WatchPageHeader } from './WatchPageHeader';

interface WatchPageClientProps {
  sidebar: WordpressSidebar | null;
  videoCategories: VideoCategoryGroup[];
}

const WatchPageClient: React.FC<WatchPageClientProps> = ({ sidebar, videoCategories }) => {
  const [activeCategory, setActiveCategory] = useState<VideoCategoryKey>(VIDEO_FILTER_CATEGORIES[0]);

  const activeVideos = useMemo(() => {
    return videoCategories.find(group => group.category === activeCategory)?.items ?? videoCategories[0]?.items ?? [];
  }, [activeCategory, videoCategories]);

  const hasOtherVideos = useMemo(() => {
    return (videoCategories.find(group => group.category === 'All')?.items.length ?? 0) > 0;
  }, [videoCategories]);

  return (
    <div className="calendar-page-layout-wrapper">
      <Layout
        className="calendar-page-layout new-design"
        layoutContentClassName="pt-2 !px-4 sm:!px-6"
        layoutMainClassName="!overflow-visible"
        layoutHeader={
          <WatchPageHeader
            activeCategory={activeCategory}
            categories={videoCategories.map(group => group.category)}
            onCategoryChange={setActiveCategory}
            videoCount={activeVideos.length}
          />
        }
        layoutHeaderOptions={{ full_width: true }}
        layoutMain={
          <div className="flex flex-col gap-8 pb-8 pt-2 max-sm:gap-6">
            <section
              aria-label={activeCategory === 'All' ? 'Latest videos' : activeCategory}
              className="flex flex-col gap-5"
            >
              <div className="flex flex-col gap-1 border-b border-[#e1ebfa] pb-1">
                <h2 className="m-0 text-xl font-extrabold leading-snug text-[#283d59]">
                  {activeCategory === 'All' ? 'Latest videos' : activeCategory}
                </h2>
                {activeVideos.length > 0 ? (
                  <p className="m-0 text-sm leading-snug text-[#5b7292]">
                    {activeCategory === 'All' ? 'The latest Benzinga TV articles' : `Browse ${activeCategory} videos`}
                  </p>
                ) : (
                  <WatchEmptyState
                    category={activeCategory}
                    hasOtherVideos={hasOtherVideos}
                    onSelectCategory={setActiveCategory}
                  />
                )}
              </div>

              {activeVideos.length > 0 ? (
                <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-2 xl:grid-cols-3">
                  {activeVideos.map(video => (
                    <WatchVideoCard href={video.href} image={video.image} key={video.id} title={video.title} />
                  ))}
                </div>
              ) : null}
            </section>
          </div>
        }
        layoutSidebar={
          <React.Suspense fallback={null}>
            {sidebar ? <MoneySidebar sidebar={sidebar} /> : <DefaultSidebar sidebar={sidebar} />}
          </React.Suspense>
        }
      />
    </div>
  );
};

export default WatchPageClient;
